import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  Switch,
  FormControlLabel,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Menu,
  MenuItem,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  RadioGroup,
  Radio,
  FormLabel,
  Checkbox,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Avatar,
  Tooltip,
  LinearProgress,
  Badge
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Add as AddIcon,
  Send as SendIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
  Key as KeyIcon,
  History as HistoryIcon,
  Settings as SettingsIcon,
  Assessment as AssessmentIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ContentCopy as CopyIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  Science as TestIcon,
  Security as SecurityIcon,
  Webhook as WebhookIcon,
  Campaign as CampaignIcon,
  Group as GroupIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import api from '../api/axios';

const PushNotificationTab = ({ companyId, projectId, project, isActive }) => {
  // Main state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeSubTab, setActiveSubTab] = useState(0);
  
  // Settings state
  const [settings, setSettings] = useState({
    enabled: false,
    vapidKeys: { publicKey: '', hasPrivateKey: false, subject: '' },
    defaultSettings: {},
    permissions: {},
    limits: {},
    webhookSettings: {},
    statistics: {}
  });
  const [settingsSaving, setSettingsSaving] = useState(false);
  
  // API Keys state
  const [apiKeys, setApiKeys] = useState([]);
  const [apiKeyDialogOpen, setApiKeyDialogOpen] = useState(false);
  const [newApiKey, setNewApiKey] = useState({ name: '', description: '', permissions: {} });
  const [generatedApiKey, setGeneratedApiKey] = useState(null);
  const [apiKeyMenuAnchor, setApiKeyMenuAnchor] = useState(null);
  const [selectedApiKey, setSelectedApiKey] = useState(null);
  
  // Send Notification state
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [notificationForm, setNotificationForm] = useState({
    title: '',
    body: '',
    detailedContent: '',
    contentType: 'text',
    recipients: { type: 'all', emails: '' },
    icon: '',
    badge: '',
    priority: 'normal',
    tags: ''
  });
  const [sending, setSending] = useState(false);
  
  // History state
  const [history, setHistory] = useState([]);
  const [historyPagination, setHistoryPagination] = useState({ page: 0, limit: 10, total: 0 });
  const [historyFilters, setHistoryFilters] = useState({ status: 'all', sentBy: 'all' });
  
  // Statistics state
  const [statistics, setStatistics] = useState({
    deliveryStats: {},
    subscriptionStats: {},
    projectStats: {}
  });

  // Load data when tab becomes active
  useEffect(() => {
    if (isActive) {
      loadAllData();
    }
  }, [isActive, companyId, projectId]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadSettings(),
        loadApiKeys(),
        loadHistory(),
        loadStatistics()
      ]);
    } catch (error) {
      console.error('Error loading push notification data:', error);
      setError('Failed to load push notification data');
    }
    setLoading(false);
  };

  const loadSettings = async () => {
    try {
      const response = await api.get(`/companies/${companyId}/projects/${projectId}/push-notifications/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const loadApiKeys = async () => {
    try {
      const response = await api.get(`/companies/${companyId}/projects/${projectId}/push-notifications/api-keys`);
      setApiKeys(response.data.apiKeys || []);
    } catch (error) {
      console.error('Error loading API keys:', error);
    }
  };

  const loadHistory = async () => {
    try {
      const params = {
        page: historyPagination.page + 1,
        limit: historyPagination.limit,
        status: historyFilters.status !== 'all' ? historyFilters.status : undefined,
        sentBy: historyFilters.sentBy !== 'all' ? historyFilters.sentBy : undefined
      };
      
      const response = await api.get(`/companies/${companyId}/projects/${projectId}/push-notifications/history`, { params });
      setHistory(response.data.history || []);
      setHistoryPagination(prev => ({ ...prev, total: response.data.pagination?.total || 0 }));
    } catch (error) {
      console.error('Error loading history:', error);
    }
  };

  const loadStatistics = async () => {
    try {
      const response = await api.get(`/companies/${companyId}/projects/${projectId}/push-notifications/stats`);
      setStatistics(response.data);
    } catch (error) {
      console.error('Error loading statistics:', error);
    }
  };

  const handleSettingsUpdate = async (updatedSettings) => {
    setSettingsSaving(true);
    try {
      const response = await api.put(
        `/companies/${companyId}/projects/${projectId}/push-notifications/settings`,
        updatedSettings
      );
      setSettings(response.data.settings);
      await loadStatistics(); // Reload stats after settings change
    } catch (error) {
      console.error('Error updating settings:', error);
      setError('Failed to update settings');
    }
    setSettingsSaving(false);
  };

  const handleCreateApiKey = async () => {
    try {
      const response = await api.post(
        `/companies/${companyId}/projects/${projectId}/push-notifications/api-keys`,
        newApiKey
      );
      setGeneratedApiKey(response.data.apiKey);
      setNewApiKey({ name: '', description: '', permissions: {} });
      await loadApiKeys();
    } catch (error) {
      console.error('Error creating API key:', error);
      setError('Failed to create API key');
    }
  };

  const handleDeleteApiKey = async (keyId) => {
    try {
      await api.delete(`/companies/${companyId}/projects/${projectId}/push-notifications/api-keys/${keyId}`);
      await loadApiKeys();
      setApiKeyMenuAnchor(null);
      setSelectedApiKey(null);
    } catch (error) {
      console.error('Error deleting API key:', error);
      setError('Failed to delete API key');
    }
  };

  const handleSendNotification = async () => {
    setSending(true);
    try {
      const payload = {
        ...notificationForm,
        recipients: {
          type: notificationForm.recipients.type,
          emails: notificationForm.recipients.type === 'specific' 
            ? notificationForm.recipients.emails.split(',').map(email => email.trim()).filter(Boolean)
            : undefined
        },
        tags: notificationForm.tags ? notificationForm.tags.split(',').map(tag => tag.trim()) : undefined
      };
      
      await api.post(
        `/companies/${companyId}/projects/${projectId}/push-notifications/send`,
        payload
      );
      
      setSendDialogOpen(false);
      setNotificationForm({
        title: '',
        body: '',
        detailedContent: '',
        contentType: 'text',
        recipients: { type: 'all', emails: '' },
        icon: '',
        badge: '',
        priority: 'normal',
        tags: ''
      });
      
      await loadHistory();
      await loadStatistics();
    } catch (error) {
      console.error('Error sending notification:', error);
      setError('Failed to send notification');
    }
    setSending(false);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'failed': return 'error';
      case 'partial': return 'warning';
      case 'processing': return 'info';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircleIcon />;
      case 'failed': return <ErrorIcon />;
      case 'partial': return <WarningIcon />;
      case 'processing': return <InfoIcon />;
      default: return <InfoIcon />;
    }
  };

  if (!isActive) {
    return null;
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={40} />
        <Box ml={2}>
          <Typography variant="body2" color="textSecondary">
            Loading push notification settings...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">Push Notification Management</Typography>
      </Box>

      {/* Sub Tabs */}
      <Paper elevation={1} sx={{ mb: 3 }}>
        <Tabs value={activeSubTab} onChange={(e, newValue) => setActiveSubTab(newValue)}>
          <Tab icon={<SettingsIcon />} label="Settings" />
          <Tab icon={<KeyIcon />} label="API Keys" />
          <Tab icon={<CampaignIcon />} label="Send" />
          <Tab icon={<HistoryIcon />} label="History" />
          <Tab icon={<AssessmentIcon />} label="Analytics" />
        </Tabs>
      </Paper>

      {/* Settings Tab */}
      {activeSubTab === 0 && (
        <Box>
          {/* Enable/Disable Toggle */}
          <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  Push Notification Status
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Enable or disable push notifications for this project
                </Typography>
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.enabled}
                    onChange={(e) => handleSettingsUpdate({ enabled: e.target.checked })}
                    disabled={settingsSaving}
                  />
                }
                label={settings.enabled ? 'Enabled' : 'Disabled'}
              />
            </Box>
            
            {settings.enabled && (
              <Alert severity="success">
                Push notifications are active for this project. Users can subscribe and receive notifications.
              </Alert>
            )}
            
            {!settings.enabled && (
              <Alert severity="info">
                Push notifications are disabled. Enable them to allow users to receive notifications.
              </Alert>
            )}
          </Paper>

          {/* VAPID Configuration */}
          <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              VAPID Keys Configuration
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              This project uses global VAPID keys shared across all projects for multi-project compatibility. 
              This ensures users can receive notifications from any project they have access to with a single subscription.
            </Typography>
            
            <Alert severity="info" sx={{ mt: 2, mb: 3 }}>
              <Typography variant="body2">
                <strong>Multi-Project Architecture:</strong> All projects share the same global VAPID keys to prevent 
                subscription conflicts when users belong to multiple projects. This is managed at the system level.
              </Typography>
            </Alert>
            
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  label="Public Key"
                  value="Using Global System Keys"
                  disabled
                  size="small"
                  helperText="Global keys are configured at the system level"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Chip 
                    label="Global Keys Active"
                    color="success"
                    size="small"
                  />
                </Box>
              </Grid>
            </Grid>

            <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
              <strong>Note:</strong> VAPID key management is handled at the system level to ensure compatibility 
              across all projects. Contact your system administrator if you need to modify VAPID key configuration.
            </Typography>
          </Paper>

          {/* Default Notification Settings */}
          <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Default Notification Settings
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Default Icon URL"
                  value={settings.defaultSettings.icon || ''}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    defaultSettings: { ...prev.defaultSettings, icon: e.target.value }
                  }))}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Default Badge URL"
                  value={settings.defaultSettings.badge || ''}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    defaultSettings: { ...prev.defaultSettings, badge: e.target.value }
                  }))}
                  size="small"
                />
              </Grid>
            </Grid>
            <Box mt={2} display="flex" justifyContent="flex-end">
              <Button
                variant="contained"
                onClick={() => handleSettingsUpdate({ defaultSettings: settings.defaultSettings })}
                disabled={settingsSaving}
              >
                {settingsSaving ? 'Saving...' : 'Save Settings'}
              </Button>
            </Box>
          </Paper>
        </Box>
      )}

      {/* API Keys Tab */}
      {activeSubTab === 1 && (
        <Box>
          <Box display="flex" justifyContent="between" alignItems="center" mb={3}>
            <Typography variant="h6">Webhook API Keys</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setApiKeyDialogOpen(true)}
              sx={{ml:2}}
            >
              Create API Key
            </Button>
          </Box>

          {apiKeys.length === 0 ? (
            <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
              <KeyIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
              <Typography variant="h6" color="textSecondary" gutterBottom>
                No API Keys Created
              </Typography>
              <Typography variant="body2" color="textSecondary" mb={3}>
                Create API keys to allow external systems to send push notifications via webhook.
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setApiKeyDialogOpen(true)}
              >
                Create First API Key
              </Button>
            </Paper>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Permissions</TableCell>
                    <TableCell>Usage</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {apiKeys.map((apiKey) => (
                    <TableRow key={apiKey.keyId}>
                      <TableCell>
                        <Typography variant="subtitle2">{apiKey.name}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          ID: {apiKey.keyId.slice(-8)}
                        </Typography>
                      </TableCell>
                      <TableCell>{apiKey.description || 'No description'}</TableCell>
                      <TableCell>
                        <Box display="flex" gap={1} flexWrap="wrap">
                          {apiKey.permissions.canSendToAll && (
                            <Chip label="Send to All" size="small" color="primary" />
                          )}
                          {apiKey.permissions.canSendToSpecific && (
                            <Chip label="Send to Specific" size="small" color="secondary" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          Total: {apiKey.usage.totalSent}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Last hour: {apiKey.usage.lastHourSent}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(apiKey.createdAt).toLocaleDateString()}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {apiKey.createdBy?.email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          onClick={(e) => {
                            setApiKeyMenuAnchor(e.currentTarget);
                            setSelectedApiKey(apiKey);
                          }}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Webhook Documentation Section */}
          <Box mt={4}>
            <Paper elevation={1} sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" mb={3}>
                <WebhookIcon sx={{ mr: 2, color: 'primary.main' }} />
                <Typography variant="h6">
                  Webhook Integration Guide
                </Typography>
              </Box>
              
              <Typography variant="body2" color="textSecondary" paragraph>
                Use our webhook API to send push notifications from your external systems. You'll need an API key (created above) to authenticate your requests.
              </Typography>

              {/* Endpoint Information */}
              <Accordion sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box display="flex" alignItems="center">
                    <SecurityIcon sx={{ mr: 1 }} />
                    <Typography variant="subtitle1">Webhook Endpoint & Authentication</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>Endpoint URL:</Typography>
                    <Paper elevation={0} sx={{ bgcolor: 'grey.100', p: 2, mb: 2, fontFamily: 'monospace' }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          POST https://staging.neoshift.ai/api/webhook/push-notifications/{projectId}
                        </Typography>
                        <Tooltip title="Copy endpoint URL">
                          <IconButton 
                            size="small" 
                            onClick={() => navigator.clipboard.writeText(`https://staging.neoshift.ai/api/webhook/push-notifications/${projectId}`)}
                          >
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Paper>
                    
                    <Typography variant="subtitle2" gutterBottom>Authentication:</Typography>
                    <Typography variant="body2" paragraph>
                      Include your API key in the request headers:
                    </Typography>
                    <Paper elevation={0} sx={{ bgcolor: 'grey.100', p: 2, mb: 2, fontFamily: 'monospace' }}>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        X-API-Key: your_api_key_here
                      </Typography>
                    </Paper>
                  </Box>
                </AccordionDetails>
              </Accordion>

              {/* Request Format */}
              <Accordion sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box display="flex" alignItems="center">
                    <SendIcon sx={{ mr: 1 }} />
                    <Typography variant="subtitle1">Request Format</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>JSON Payload:</Typography>
                    <Paper elevation={0} sx={{ bgcolor: 'grey.100', p: 2, mb: 2, fontFamily: 'monospace', fontSize: '0.875rem' }}>
                      <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
{`{
  "title": "Your notification title",
  "body": "Your notification message",
  "detailedContent": "Extended content for in-app display...", // optional, max 10,000 chars
  "contentType": "text" | "html" | "markdown", // optional, default: "text"
  "icon": "/icons/notification-icon.png", // optional
  "url": "https://yourapp.com/landing", // optional
  "recipients": {
    "type": "all" | "specific",
    "emails": ["user1@email.com", "user2@email.com"] // required if type = "specific"
  },
  "priority": "high" | "normal" | "low", // optional, default: "normal"
  "tags": ["urgent", "marketing", "updates"], // optional, for categorization
  "data": { // optional custom data
    "customField": "customValue"
  }
}`}
                      </pre>
                    </Paper>
                    
                    <Typography variant="subtitle2" gutterBottom>Required Fields:</Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                        <ListItemText primary="title" secondary="Notification title (max 100 characters)" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                        <ListItemText primary="body" secondary="Notification message (max 500 characters)" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                        <ListItemText primary="recipients" secondary="Who should receive the notification" />
                      </ListItem>
                    </List>
                    
                    <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>Optional Fields:</Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon><InfoIcon color="info" fontSize="small" /></ListItemIcon>
                        <ListItemText primary="detailedContent" secondary="Extended content for in-app display (max 10,000 characters)" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><InfoIcon color="info" fontSize="small" /></ListItemIcon>
                        <ListItemText primary="contentType" secondary="Format of detailedContent: 'text', 'html', or 'markdown'" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><InfoIcon color="info" fontSize="small" /></ListItemIcon>
                        <ListItemText primary="tags" secondary="Array of strings for categorization and filtering" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><InfoIcon color="info" fontSize="small" /></ListItemIcon>
                        <ListItemText primary="priority" secondary="Notification priority: 'low', 'normal', 'high', or 'urgent'" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><InfoIcon color="info" fontSize="small" /></ListItemIcon>
                        <ListItemText primary="icon" secondary="Custom icon URL for the notification" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><InfoIcon color="info" fontSize="small" /></ListItemIcon>
                        <ListItemText primary="url" secondary="URL to open when notification is clicked" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><InfoIcon color="info" fontSize="small" /></ListItemIcon>
                        <ListItemText primary="data" secondary="Custom data object for additional payload" />
                      </ListItem>
                    </List>
                  </Box>
                </AccordionDetails>
              </Accordion>

              {/* Code Examples */}
              <Accordion sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box display="flex" alignItems="center">
                    <TestIcon sx={{ mr: 1 }} />
                    <Typography variant="subtitle1">Code Examples</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box>
                    <Tabs value={0} sx={{ mb: 2 }}>
                      <Tab label="cURL" />
                      <Tab label="JavaScript" />
                      <Tab label="Python" />
                      <Tab label="PHP" />
                    </Tabs>
                    
                    {/* cURL Example */}
                    <Paper elevation={0} sx={{ bgcolor: 'grey.100', p: 2, mb: 2, fontFamily: 'monospace', fontSize: '0.875rem' }}>
                      <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
{`curl -X POST https://staging.neoshift.ai/api/push-notifications/webhook/push-notifications/${projectId} \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: your_api_key_here" \\
  -d '{
    "title": "New Order Received",
    "body": "You have a new order #12345 that needs attention",
    "detailedContent": "Order Details:\\n• Product: Premium Widget\\n• Quantity: 5\\n• Total: $299.99\\n• Customer: John Doe\\n• Expected Delivery: Tomorrow\\n\\nPlease process this order within 2 hours.",
    "contentType": "text",
    "recipients": {
      "type": "specific",
      "emails": ["admin@yourstore.com"]
    },
    "priority": "high",
    "tags": ["orders", "urgent", "admin-alert"],
    "url": "https://yourstore.com/orders/12345"
  }'`}
                      </pre>
                    </Paper>

                    {/* JavaScript Example */}
                    <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>JavaScript (fetch):</Typography>
                    <Paper elevation={0} sx={{ bgcolor: 'grey.100', p: 2, mb: 2, fontFamily: 'monospace', fontSize: '0.875rem' }}>
                      <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
{`const response = await fetch('https://staging.neoshift.ai/api/push-notifications/webhook/push-notifications/${projectId}', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'X-API-Key': 'your_api_key_here'
  },
  body: JSON.stringify({
    title: 'System Alert',
    body: 'Server maintenance will begin in 30 minutes',
    detailedContent: 'Scheduled Maintenance Details:\\n\\n• Start Time: 2:00 AM EST\\n• Expected Duration: 2 hours\\n• Services Affected: API, Dashboard\\n• Backup systems will remain operational\\n\\nPlease complete any critical tasks before maintenance begins.',
    contentType: 'text',
    recipients: { type: 'all' },
    priority: 'high',
    tags: ['maintenance', 'system-alert', 'urgent']
  })
});

const result = await response.json();
console.log('Notification sent:', result);`}
                      </pre>
                    </Paper>
                  </Box>
                </AccordionDetails>
              </Accordion>

              {/* Response Examples */}
              <Accordion sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box display="flex" alignItems="center">
                    <InfoIcon sx={{ mr: 1 }} />
                    <Typography variant="subtitle1">Response Examples</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>Success Response (200):</Typography>
                    <Paper elevation={0} sx={{ bgcolor: 'green.50', p: 2, mb: 2, fontFamily: 'monospace', fontSize: '0.875rem' }}>
                      <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
{`{
  "success": true,
  "message": "Push notification sent successfully",
  "data": {
    "notificationId": "notif_abc123",
    "totalSent": 25,
    "totalDelivered": 23,
    "totalFailed": 2,
    "timestamp": "2025-01-10T15:30:00Z"
  }
}`}
                      </pre>
                    </Paper>

                    <Typography variant="subtitle2" gutterBottom>Error Response (400):</Typography>
                    <Paper elevation={0} sx={{ bgcolor: 'red.50', p: 2, mb: 2, fontFamily: 'monospace', fontSize: '0.875rem' }}>
                      <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
{`{
  "success": false,
  "error": "Validation failed",
  "details": {
    "title": "Title is required",
    "body": "Body cannot exceed 500 characters"
  }
}`}
                      </pre>
                    </Paper>

                    <Typography variant="subtitle2" gutterBottom>Authentication Error (401):</Typography>
                    <Paper elevation={0} sx={{ bgcolor: 'orange.50', p: 2, fontFamily: 'monospace', fontSize: '0.875rem' }}>
                      <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
{`{
  "success": false,
  "error": "Invalid or missing API key"
}`}
                      </pre>
                    </Paper>
                  </Box>
                </AccordionDetails>
              </Accordion>

              {/* Testing */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box display="flex" alignItems="center">
                    <TestIcon sx={{ mr: 1 }} />
                    <Typography variant="subtitle1">Testing Your Integration</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box>
                    <Typography variant="body2" paragraph>
                      Follow these steps to test your webhook integration:
                    </Typography>
                    
                    <List>
                      <ListItem>
                        <ListItemIcon>
                          <Box 
                            sx={{ 
                              width: 24, 
                              height: 24, 
                              borderRadius: '50%', 
                              bgcolor: 'primary.main', 
                              color: 'white', 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center',
                              fontSize: '0.875rem'
                            }}
                          >
                            1
                          </Box>
                        </ListItemIcon>
                        <ListItemText 
                          primary="Create an API Key" 
                          secondary="Use the 'Create API Key' button above to generate a new webhook API key" 
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <Box 
                            sx={{ 
                              width: 24, 
                              height: 24, 
                              borderRadius: '50%', 
                              bgcolor: 'primary.main', 
                              color: 'white', 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center',
                              fontSize: '0.875rem'
                            }}
                          >
                            2
                          </Box>
                        </ListItemIcon>
                        <ListItemText 
                          primary="Test with cURL" 
                          secondary="Copy the cURL example above and replace 'your_api_key_here' with your actual API key" 
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <Box 
                            sx={{ 
                              width: 24, 
                              height: 24, 
                              borderRadius: '50%', 
                              bgcolor: 'primary.main', 
                              color: 'white', 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center',
                              fontSize: '0.875rem'
                            }}
                          >
                            3
                          </Box>
                        </ListItemIcon>
                        <ListItemText 
                          primary="Check Delivery" 
                          secondary="Verify that notifications are received on subscribed devices and check the History tab for delivery status" 
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <Box 
                            sx={{ 
                              width: 24, 
                              height: 24, 
                              borderRadius: '50%', 
                              bgcolor: 'primary.main', 
                              color: 'white', 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center',
                              fontSize: '0.875rem'
                            }}
                          >
                            4
                          </Box>
                        </ListItemIcon>
                        <ListItemText 
                          primary="Monitor Usage" 
                          secondary="Track your API key usage and notification delivery rates in the Analytics tab" 
                        />
                      </ListItem>
                    </List>

                    <Alert severity="info" sx={{ mt: 2 }}>
                      <Typography variant="body2">
                        <strong>Pro Tip:</strong> Start by sending notifications to yourself using the "specific" recipient type before integrating with your production systems.
                      </Typography>
                    </Alert>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Paper>
          </Box>
        </Box>
      )}

      {/* Send Notification Tab */}
      {activeSubTab === 2 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Send Push Notification
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Send push notifications to all project users or specific email addresses. Use detailed content for rich information that will be displayed in the client app's notifications tab.
          </Typography>

          <Paper elevation={1} sx={{ p: 3 }}>
            <Grid container spacing={3}>

                <FormControl fullWidth>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={notificationForm.priority}
                    onChange={(e) => setNotificationForm(prev => ({ ...prev, priority: e.target.value }))}
                  >
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="normal">Normal</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="urgent">Urgent</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="Notification Title"
                  value={notificationForm.title}
                  onChange={(e) => setNotificationForm(prev => ({ ...prev, title: e.target.value }))}
                  required
                />
                <TextField
                  fullWidth
                  label="Tags (comma-separated)"
                  value={notificationForm.tags}
                  onChange={(e) => setNotificationForm(prev => ({ ...prev, tags: e.target.value }))}
                  placeholder="marketing, updates, alerts"
                  sx={{mb:1}}
                />
                <TextField
                  fullWidth
                  multiline
                  rows={5}
                  label="Notification Body"
                  value={notificationForm.body}
                  onChange={(e) => setNotificationForm(prev => ({ ...prev, body: e.target.value }))}
                  required
                  helperText="Short message for the push notification (max 500 characters)"
                />

                <TextField
                  fullWidth
                  multiline
                  rows={8}
                  label="Detailed Content (Optional)"
                  value={notificationForm.detailedContent}
                  onChange={(e) => setNotificationForm(prev => ({ ...prev, detailedContent: e.target.value }))}
                  placeholder="Extended content that will be shown in the notifications tab of the client app..."
                  helperText="Extended content for in-app display (max 10,000 characters)"
                  inputProps={{ maxLength: 10000 }}
                />

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Content Type</InputLabel>
                  <Select
                    value={notificationForm.contentType}
                    onChange={(e) => setNotificationForm(prev => ({ ...prev, contentType: e.target.value }))}
                    label="Content Type"
                  >
                    <MenuItem value="text">Plain Text</MenuItem>
                    <MenuItem value="html">HTML</MenuItem>
                    <MenuItem value="markdown">Markdown</MenuItem>
                  </Select>
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
                    Format for the detailed content display
                  </Typography>
                </FormControl>

                <FormControl component="fieldset">
                  <FormLabel component="legend">Recipients</FormLabel>
                  <RadioGroup
                    value={notificationForm.recipients.type}
                    onChange={(e) => setNotificationForm(prev => ({
                      ...prev,
                      recipients: { ...prev.recipients, type: e.target.value }
                    }))}
                  >
                    <FormControlLabel value="all" control={<Radio />} label="All project users" />
                    <FormControlLabel value="specific" control={<Radio />} label="Specific email addresses" />
                  </RadioGroup>
                </FormControl>
                {notificationForm.recipients.type === 'specific' && (
                  <TextField
                    fullWidth
                    sx={{mb:1}}
                    label="Email Addresses (comma-separated)"
                    value={notificationForm.recipients.emails}
                    onChange={(e) => setNotificationForm(prev => ({
                      ...prev,
                      recipients: { ...prev.recipients, emails: e.target.value }
                    }))}
                    placeholder="user1@example.com, user2@example.com"
                  />
              )}
            </Grid>
            
            <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
              <Button
                variant="outlined"
                onClick={() => setNotificationForm({
                  title: '',
                  body: '',
                  detailedContent: '',
                  contentType: 'text',
                  recipients: { type: 'all', emails: '' },
                  icon: '',
                  badge: '',
                  priority: 'normal',
                  tags: ''
                })}
              >
                Clear
              </Button>
              <Button
                variant="contained"
                startIcon={<SendIcon />}
                onClick={handleSendNotification}
                disabled={!notificationForm.title || !notificationForm.body || sending}
              >
                {sending ? 'Sending...' : 'Send Notification'}
              </Button>
            </Box>
          </Paper>
        </Box>
      )}

      {/* History Tab */}
      {activeSubTab === 3 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Notification History
          </Typography>
          
          {/* Filters */}
          <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={historyFilters.status}
                    onChange={(e) => setHistoryFilters(prev => ({ ...prev, status: e.target.value }))}
                  >
                    <MenuItem value="all">All Statuses</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="failed">Failed</MenuItem>
                    <MenuItem value="partial">Partial</MenuItem>
                    <MenuItem value="processing">Processing</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Sent By</InputLabel>
                  <Select
                    value={historyFilters.sentBy}
                    onChange={(e) => setHistoryFilters(prev => ({ ...prev, sentBy: e.target.value }))}
                  >
                    <MenuItem value="all">All Sources</MenuItem>
                    <MenuItem value="admin">Admin</MenuItem>
                    <MenuItem value="webhook">Webhook</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <Button
                  variant="outlined"
                  onClick={loadHistory}
                  fullWidth
                >
                  Apply Filters
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* History Table */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Notification</TableCell>
                  <TableCell>Recipients</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Delivery</TableCell>
                  <TableCell>Sent By</TableCell>
                  <TableCell>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((notification) => (
                  <TableRow key={notification.notificationId}>
                    <TableCell>
                      <Typography variant="subtitle2" gutterBottom>
                        {notification.title}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {notification.body.substring(0, 60)}...
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {notification.recipients.type === 'all' ? <GroupIcon /> : <PersonIcon />}
                        <Typography variant="body2">
                          {notification.recipients.type === 'all' 
                            ? 'All users' 
                            : `${notification.recipients.emails?.length || 0} users`}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(notification.status)}
                        label={notification.status}
                        color={getStatusColor(notification.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {notification.delivery.totalDelivered}/{notification.delivery.totalSent}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {notification.delivery.deliveryRate}% success
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={notification.sentBy.type}
                        size="small"
                        variant="outlined"
                      />
                      {notification.sentBy.apiKeyName && (
                        <Typography variant="caption" color="textSecondary" display="block">
                          {notification.sentBy.apiKeyName}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(notification.timing.sentAt).toLocaleDateString()}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {new Date(notification.timing.sentAt).toLocaleTimeString()}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={historyPagination.total}
              page={historyPagination.page}
              onPageChange={(e, newPage) => setHistoryPagination(prev => ({ ...prev, page: newPage }))}
              rowsPerPage={historyPagination.limit}
              onRowsPerPageChange={(e) => setHistoryPagination(prev => ({ 
                ...prev, 
                limit: parseInt(e.target.value),
                page: 0 
              }))}
            />
          </TableContainer>
        </Box>
      )}

      {/* Analytics Tab */}
      {activeSubTab === 4 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Push Notification Analytics
          </Typography>
          
          {/* Overview Cards */}
          <Grid container spacing={3} mb={4}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      <SendIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h4" fontWeight="bold">
                        {statistics.deliveryStats.totalSent || 0}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Total Sent
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Avatar sx={{ bgcolor: 'success.main' }}>
                      <CheckCircleIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h4" fontWeight="bold">
                        {Math.round(statistics.deliveryStats.avgDeliveryRate || 0)}%
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Delivery Rate
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Avatar sx={{ bgcolor: 'info.main' }}>
                      <GroupIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h4" fontWeight="bold">
                        {statistics.subscriptionStats.activeSubscriptions || 0}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Active Subscribers
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Avatar sx={{ bgcolor: 'warning.main' }}>
                      <AssessmentIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h4" fontWeight="bold">
                        {Math.round(statistics.subscriptionStats.avgClickThroughRate || 0)}%
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Click Through Rate
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* API Key Creation Dialog */}
      <Dialog open={apiKeyDialogOpen} onClose={() => setApiKeyDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create API Key</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <TextField
              label="API Key Name"
              value={newApiKey.name}
              onChange={(e) => setNewApiKey(prev => ({ ...prev, name: e.target.value }))}
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={newApiKey.description}
              onChange={(e) => setNewApiKey(prev => ({ ...prev, description: e.target.value }))}
              multiline
              rows={2}
              fullWidth
            />
            <Typography variant="subtitle2" gutterBottom>
              Permissions
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={newApiKey.permissions.canSendToAll || false}
                  onChange={(e) => setNewApiKey(prev => ({
                    ...prev,
                    permissions: { ...prev.permissions, canSendToAll: e.target.checked }
                  }))}
                />
              }
              label="Can send to all users"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={newApiKey.permissions.canSendToSpecific || false}
                  onChange={(e) => setNewApiKey(prev => ({
                    ...prev,
                    permissions: { ...prev.permissions, canSendToSpecific: e.target.checked }
                  }))}
                />
              }
              label="Can send to specific users"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApiKeyDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateApiKey}
            variant="contained"
            disabled={!newApiKey.name}
          >
            Create API Key
          </Button>
        </DialogActions>
      </Dialog>

      {/* Generated API Key Dialog */}
      <Dialog 
        open={!!generatedApiKey} 
        onClose={() => setGeneratedApiKey(null)}
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>API Key Created Successfully</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This is the only time the API key will be shown. Please save it securely.
          </Alert>
          <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1, mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              API Key:
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" fontFamily="monospace" sx={{ flex: 1 }}>
                {generatedApiKey?.apiKey}
              </Typography>
              <IconButton
                size="small"
                onClick={() => copyToClipboard(generatedApiKey?.apiKey)}
              >
                <CopyIcon />
              </IconButton>
            </Box>
          </Box>
          <Typography variant="body2" color="textSecondary">
            Use this API key in the Authorization header: Bearer {generatedApiKey?.apiKey}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGeneratedApiKey(null)} variant="contained">
            I've Saved the Key
          </Button>
        </DialogActions>
      </Dialog>

      {/* API Key Menu */}
      <Menu
        anchorEl={apiKeyMenuAnchor}
        open={Boolean(apiKeyMenuAnchor)}
        onClose={() => setApiKeyMenuAnchor(null)}
      >
        <MenuItem onClick={() => handleDeleteApiKey(selectedApiKey?.keyId)}>
          <DeleteIcon sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default PushNotificationTab;